import {useAui} from '@assistant-ui/react';
import {useLayoutEffect, useRef} from 'react';

import type {ActionResult} from '../../agentChat/actionResult';
import {
  draftKey,
  type SelectedPanel,
  type SelectedRun,
  useDraftStore,
} from '../../agentChat/composer';
import type {
  SendMessageOptions,
  WBAgentController,
} from '../../agentChat/controller';
import {wbAgentControllerStore} from '../../agentChat/controller';
import {
  isUserRepresentable,
  toUserMessageContent,
} from '../../agentChat/messageConversions';
import type {AgentChatStore} from '../../agentChat/store';
import {
  hasMeaningfulContent,
  selectActiveThread,
  useAgentChatStore,
} from '../../agentChat/store';
import {selectIsOnNewChat} from '../../agentChat/store/selectors';
import type {ContentPart} from '../../agentChat/store/thread';
import {resolveHITLSendBlock} from '../../agentChat/tools/catalog';
import {isAgentChatReadonly} from '../../agentChat/useAgentChatReadonly';
import {closeCustomizationSection} from './customizations/useCustomizationsStore';
import {useTrackWBAgentComposer} from './useTrackWBAgent';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

type Aui = ReturnType<typeof useAui>;
type TrackSent = ReturnType<typeof useTrackWBAgentComposer>['trackMessageSent'];

/**
 * WBAgentControllerBridge registers the imperative controller handle while the
 * agent chat is mounted, so buttons anywhere in the app can open the chat or
 * send a message. It must render inside `AuiProvider` (it reads `useAui`) and
 * is mounted only when the feature is available; the handle is cleared on
 * unmount. Renders nothing.
 */
export const WBAgentControllerBridge = (): null => {
  const aui = useAui();
  const store = useAgentChatStore();
  const {trackMessageSent} = useTrackWBAgentComposer();

  // NOTE: The ref keeps the tracker out of the effect deps, so the handle
  // re-registers only on `aui` changes.
  const trackRef = useRef(trackMessageSent);
  trackRef.current = trackMessageSent;

  // NOTE: Layout effect clears the handle before paint, beating any click that
  // would otherwise reach a handle whose runtime is being torn down.
  useLayoutEffect(
    () =>
      wbAgentControllerStore.registerHandle(
        createController(aui, store, () => trackRef.current)
      ),
    [aui, store]
  );

  return null;
};

function createController(
  aui: Aui,
  store: AgentChatStore,
  getTrackSent: () => TrackSent
): WBAgentController {
  const openChat = () => {
    useWBAgentWindowStore.getState().setOpen(true);
    closeCustomizationSection();
  };
  const addToChatDraft = (
    add: (key: string) => void
  ): ActionResult<'readonly'> => {
    if (isAgentChatReadonly()) {
      return {ok: false, reason: 'readonly'};
    }
    const state = store.getState();
    const shouldStartFresh =
      !useWBAgentWindowStore.getState().open && !selectIsOnNewChat(state);
    if (shouldStartFresh) {
      aui.threads().switchToNewThread();
    }
    const threadId = selectActiveThread(store.getState())?.id ?? null;
    add(draftKey(threadId));
    return {ok: true};
  };

  return {
    addPanelToChat(panel: SelectedPanel) {
      return addToChatDraft(key =>
        useDraftStore.getState().addPanel(key, panel)
      );
    },
    addRunToChat(run: SelectedRun) {
      return addToChatDraft(key => useDraftStore.getState().addRun(key, run));
    },
    openChat,
    openChatToThread(threadId: string) {
      aui.threads().switchToThread(threadId);
      openChat();
    },
    sendMessage(
      prompt: string | ContentPart[],
      attachments?: ContentPart[],
      opts?: SendMessageOptions
    ) {
      const {newChat = true} = opts ?? {};
      const parts = messageParts(prompt, attachments);

      // Nothing to send is a no-op; don't open or switch threads.
      if (!hasMeaningfulContent(parts.filter(isUserRepresentable))) {
        return {ok: false, reason: 'empty'};
      }

      // A pending HITL tool call on the active thread blocks every send path;
      // consult the same leaf-anchored resolver the runtime enforces so this
      // entry point can't report success on a send the runtime drops.
      if (
        !newChat &&
        resolveHITLSendBlock(selectActiveThread(store.getState()))
      ) {
        return {ok: false, reason: 'blocked'};
      }

      if (newChat) {
        aui.threads().switchToNewThread();
      }

      const readonly = isAgentChatReadonly();
      if (!readonly) {
        const content = toUserMessageContent(parts);
        aui.thread().append({role: 'user', content});
        getTrackSent()('programmatic');
      }

      openChat();
      return readonly ? {ok: false, reason: 'readonly'} : {ok: true};
    },
  };
}

// Normalize the two call shapes into the ordered parts of one user message. A
// parts array is already the full composite message (text + structured context
// in the caller's order). A string keeps the legacy shape: leading attachments,
// then the trimmed text.
function messageParts(
  prompt: string | ContentPart[],
  attachments?: ContentPart[]
): ContentPart[] {
  if (typeof prompt !== 'string') {
    return prompt;
  }
  const parts: ContentPart[] = [...(attachments ?? [])];
  const text = prompt.trim();
  if (text.length > 0) {
    parts.push({type: 'text', text, status: 'complete'});
  }
  return parts;
}
